// Top bar with search, notifications, dark toggle, user menu.
import { useState } from "react";
import { I } from "../shared/Icon";
import { useAuth } from "../shared/stores/auth";
import { useTheme } from "../shared/stores/theme";
import { useNavigate } from "react-router-dom";
import { authApi } from "../shared/api/auth";
import { toast } from "../shared/stores/toasts";
import { APP_INSTALL_URL, isInsideTimboxApp } from "../shared/nativeApp";

type Props = {
  onSearch?: () => void;
  onMenu?: () => void;
  isMobile?: boolean;
  notifCount?: number;
  onNotifClick?: () => void;
  onAiClick?: () => void;
  aiOpen?: boolean;
};

export function TopBar({ onSearch, onMenu, isMobile, notifCount = 0, onNotifClick, onAiClick, aiOpen }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const dark = useTheme((s) => s.dark);
  const setTheme = useTheme((s) => s.set);
  const user = useAuth((s) => s.user);
  const refresh = useAuth((s) => s.refreshToken);
  const logout = useAuth((s) => s.logout);
  const nav = useNavigate();

  const initials = (user?.name || "TIM").slice(0, 2);

  const doLogout = async () => {
    try { if (refresh) await authApi.logout(refresh); } catch { /* ignore */ }
    logout();
    toast.info("로그아웃 되었습니다");
    nav("/login");
  };

  return (
    <div className="t-topbar">
      {isMobile && (
        <button className="t-iconbtn" aria-label="메뉴" onClick={onMenu}>
          <I.Menu size={20}/>
        </button>
      )}

      <div className="t-logo" role="button" style={{ background: dark ? "#FFFFFF" : "transparent", padding: dark ? "2px 6px" : 0, borderRadius: 6, cursor: "pointer" }} onClick={() => nav("/dashboard")}>
        <img src="/timple-logo.jpg" alt="TIMPLE" style={{ height: isMobile ? 22 : 26 }} />
      </div>

      {!isMobile && (
        <div className="t-search" onClick={onSearch}>
          <I.Search size={15}/>
          <span>임직원 · 메일 · 결재 · 파일 검색…</span>
          <span className="kbd">⌘K</span>
        </div>
      )}

      {isMobile && <div className="t-spacer"/>}

      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {isMobile && <button className="t-iconbtn" onClick={onSearch}><I.Search size={18}/></button>}
        <button className="t-iconbtn" onClick={() => setTheme({ dark: !dark })} title={dark ? "라이트 모드" : "다크 모드"}>
          {dark ? <I.Sun size={18}/> : <I.Moon size={18}/>}
        </button>
        <button
          className="t-iconbtn"
          onClick={onAiClick}
          title="AI 어시스턴트"
          style={aiOpen ? { color: "var(--p-500)", background: "var(--p-50, color-mix(in srgb, var(--p-500) 10%, transparent))" } : undefined}
        >
          <I.Robot size={18}/>
        </button>
        <button className="t-iconbtn" onClick={onNotifClick}>
          <I.Bell size={18}/>
          {notifCount > 0 && <span className="dot"/>}
        </button>
        <button className="t-avatar" onClick={() => setMenuOpen((o) => !o)}>{initials}</button>
      </div>

      {menuOpen && (
        <>
          <div style={{ position: "absolute", inset: 0, zIndex: 50 }} onClick={() => setMenuOpen(false)} />
          <div className="t-menu">
            <div className="me">
              <div className="av">{initials}</div>
              <div>
                <div className="name">{user?.name ?? "사용자"}</div>
                <div className="role">{user?.email}</div>
              </div>
            </div>
            <button className="mi" onClick={() => { setMenuOpen(false); nav("/settings"); }}>
              <I.User size={16}/> 환경설정
            </button>
            <button className="mi" onClick={() => { setMenuOpen(false); nav("/auth/webauthn"); }}>
              <I.Key size={16}/> 패스키 관리
            </button>
            {/* 설치 앱 받기 — 이미 앱 안이면 숨긴다. 공개 페이지라 새 탭으로 연다. */}
            {!isInsideTimboxApp() && (
              <button className="mi" onClick={() => {
                setMenuOpen(false);
                window.open(APP_INSTALL_URL, "_blank", "noopener,noreferrer");
              }}>
                <I.Cloud size={16}/> 앱 받기 (윈도우·안드로이드)
              </button>
            )}
            <hr/>
            <button className="mi danger" onClick={doLogout}><I.Logout size={16}/> 로그아웃</button>
          </div>
        </>
      )}
    </div>
  );
}
